import { SYNC_PROTOCOL_VERSION } from '@doneline/shared';
import {
  type HealthResponse,
  type PullV2Response,
  type PushRequest,
  type PushV2Response,
  healthResponseSchema,
  pullV2ResponseSchema,
  pushV2ResponseSchema,
} from './protocol.js';

export interface SyncTransportOptions {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

// 请求超时：无超时的裸 fetch 会在半开连接 / RN okhttp（read timeout=0）下永久 hang——
// engine 的 pushing/pulling 闩只在 finally 复位，一旦请求卡死，闩永不复位，后续
// pulse/focus/online 触发全部静默早退（见 local-first-guardrails.md A 节的同步通道假设）。
// push/pull 走批量端点，给足 30s；health 是轻量探活，给短一点更快失败。
const PUSH_TIMEOUT_MS = 30_000;
const PULL_TIMEOUT_MS = 30_000;
const HEALTH_TIMEOUT_MS = 10_000;

// AbortController + setTimeout 而非 AbortSignal.timeout()：后者在 RN / 旧 WebKit 上的
// 运行时支持面不确定，前者是最大兼容写法。超时触发后把 abort 归一成一个普通 Error
// （而非 SyncTransportError）——engine 的 catch 分支里非 SyncTransportError 一律落入
// 现有「网络错误」路径（setStatus('offline', ...)，outbox 保留、闩在 finally 复位），
// 语义与裸网络失败完全一致，不需要 engine 侧新增分支。
//
// body 读取也必须在同一个超时窗口内（R30）：浏览器/WKWebView 的流式 fetch 在
// **响应头到达**时就 resolve，res.json() 是其后的第二段 IO——若只给 fetch 挂
// signal、在 fetch resolve 后就 clearTimeout，body 中途 stall（半开连接送完
// header 卡住）会绕过超时永久挂起 pushing/pulling 闩。这里把 json 解析并入窗口，
// 用 Promise.race 保证 abort 时 await 一定落定（不依赖各运行时「abort 会 reject
// 进行中的 body 读取」的实现差异）。RN 的 fetch 基于 XHR、body 收完才 resolve，
// 天然不受此坑影响，但统一路径无害。
async function fetchJsonWithTimeout(
  fetchImpl: typeof fetch,
  input: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<{ ok: boolean; status: number; json: unknown }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const aborted = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(new Error('aborted')), {
      once: true,
    });
  });
  // 提前挂 no-op handler：若 fetch 自身先 reject（header 永不到达路径），aborted 从未被
  // race 消费，超时触发时它的 rejection 会变成 unhandled rejection——这里先钉一个空
  // handler（不影响后续 race 正常收到 rejection）。
  aborted.catch(() => {});
  try {
    const res = await fetchImpl(input, { ...init, signal: controller.signal });
    if (!res.ok) {
      // 错误体解析失败降级 null（原 safeJson 语义），但 stall 仍要能被超时打断。
      const errBody = await Promise.race([safeJson(res), aborted]);
      return { ok: false, status: res.status, json: errBody };
    }
    // ok 路径保持原语义：body 不是合法 JSON 时抛 SyntaxError 向上传播。
    const json = await Promise.race([res.json(), aborted]);
    return { ok: true, status: res.status, json };
  } catch (err) {
    if (controller.signal.aborted) {
      throw new Error(`sync request to ${input} timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export class SyncTransportError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'SyncTransportError';
    this.status = status;
    this.body = body;
  }
}

export class SyncProtocolVersionError extends Error {
  readonly requiredVersion = SYNC_PROTOCOL_VERSION;
  readonly receivedVersion: unknown;

  constructor(receivedVersion: unknown) {
    super(
      `sync server protocol is incompatible: required v${SYNC_PROTOCOL_VERSION}, received ${
        receivedVersion ?? 'legacy v1'
      }`,
    );
    this.name = 'SyncProtocolVersionError';
    this.receivedVersion = receivedVersion;
  }
}

function parseV2Response<T>(
  schema: {
    safeParse: (value: unknown) => { success: true; data: T } | { success: false; error: Error };
  },
  value: unknown,
): T {
  const parsed = schema.safeParse(value);
  if (parsed.success) return parsed.data;
  const received =
    typeof value === 'object' && value !== null && 'protocol_version' in value
      ? (value as { protocol_version?: unknown }).protocol_version
      : undefined;
  if (received !== SYNC_PROTOCOL_VERSION) {
    throw new SyncProtocolVersionError(received);
  }
  throw parsed.error;
}

export class SyncTransport {
  private readonly baseUrl: string;
  private readonly getAccessToken: () => Promise<string | null>;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: SyncTransportOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.getAccessToken = opts.getAccessToken;
    // Native fetch must run with globalThis as `this`. Storing `fetch` as a
    // class property and invoking via `this.fetchImpl(...)` strips the
    // binding and browsers throw "Illegal invocation". Bind explicitly.
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async push(body: PushRequest): Promise<PushV2Response> {
    const res = await fetchJsonWithTimeout(
      this.fetchImpl,
      `${this.baseUrl}/sync/push`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.authHeaders()),
        },
        body: JSON.stringify({
          ...body,
          protocol_version: SYNC_PROTOCOL_VERSION,
        }),
      },
      PUSH_TIMEOUT_MS,
    );
    if (!res.ok) {
      throw new SyncTransportError(`push failed: ${res.status}`, res.status, res.json);
    }
    return parseV2Response(pushV2ResponseSchema, res.json);
  }

  async pull(
    sinceVersion: number,
    limit = 500,
    opts?: { deviceId?: string; snapshot?: boolean; snapshotCutoff?: number; locale?: string },
  ): Promise<PullV2Response> {
    const url = new URL(`${this.baseUrl}/sync/pull`);
    url.searchParams.set('protocol_version', String(SYNC_PROTOCOL_VERSION));
    url.searchParams.set('since_version', String(sinceVersion));
    url.searchParams.set('limit', String(limit));
    if (opts?.deviceId) url.searchParams.set('device_id', opts.deviceId);
    if (opts?.snapshot) url.searchParams.set('snapshot', 'true');
    if (opts?.snapshotCutoff !== undefined) {
      url.searchParams.set('snapshot_cutoff', String(opts.snapshotCutoff));
    }
    // 服务端只在 since_version=0（bootstrap）时用它挑引导数据的语言；其余请求带上无副作用。
    if (opts?.locale) url.searchParams.set('locale', opts.locale);
    const res = await fetchJsonWithTimeout(
      this.fetchImpl,
      url.toString(),
      {
        method: 'GET',
        headers: await this.authHeaders(),
      },
      PULL_TIMEOUT_MS,
    );
    if (!res.ok) {
      throw new SyncTransportError(`pull failed: ${res.status}`, res.status, res.json);
    }
    return parseV2Response(pullV2ResponseSchema, res.json);
  }

  async health(): Promise<HealthResponse> {
    const res = await fetchJsonWithTimeout(
      this.fetchImpl,
      `${this.baseUrl}/sync/health`,
      {
        method: 'GET',
        headers: await this.authHeaders(),
      },
      HEALTH_TIMEOUT_MS,
    );
    if (!res.ok) {
      throw new SyncTransportError(`health failed: ${res.status}`, res.status, res.json);
    }
    return healthResponseSchema.parse(res.json);
  }
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
